export function Services() {
  const sessions = [
    { name: 'Monthly Review', frequency: 'Once a month', icon: 'ri-calendar-line', description: 'Comprehensive monthly clarity session' },
    { name: 'Fortnightly Review', frequency: 'Every 2 weeks', icon: 'ri-calendar-2-line', description: 'Bi-weekly guidance and support' },
    { name: 'Weekly Review', frequency: 'Every week', icon: 'ri-calendar-check-line', description: 'Weekly clarity and progress tracking' },
    { name: 'Bi-weekly Review', frequency: 'Twice a week', icon: 'ri-calendar-event-line', description: 'Intensive bi-weekly sessions' }
  ];

  const courses = [
    { 
      name: 'Process Course', 
      level: 'Foundation', 
      icon: 'ri-book-open-line',
      description: 'Understanding the fundamental process of gaining clarity in arranged marriage decisions'
    },
    { 
      name: 'Nuanced Course', 
      level: 'Intermediate', 
      icon: 'ri-book-2-line',
      description: 'Deep dive into the subtle nuances and complexities of partner evaluation'
    },
    { 
      name: 'Detailed Course', 
      level: 'Advanced', 
      icon: 'ri-book-3-line',
      description: 'Comprehensive detailed analysis and advanced clarity techniques'
    }
  ];

  const clips = [
    { 
      name: 'QnA Sessions', 
      icon: 'ri-question-answer-line',
      description: 'Quick answers to your arranged marriage clarity questions'
    },
    { 
      name: 'Live Sessions', 
      icon: 'ri-live-line',
      description: 'Interactive live sessions on various clarity topics'
    },
    { 
      name: 'Blogs', 
      icon: 'ri-article-line',
      description: 'In-depth articles and insights on arranged marriage clarity'
    }
  ];

  return (
    <section id="services" className="py-24 bg-gradient-to-b from-white via-rose-50/30 to-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-6">
            <div className="flex items-center gap-2 px-4 py-2 bg-rose-100 rounded-full">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-service-line text-xl text-rose-800"></i>
              </div>
              <span className="text-sm font-semibold text-rose-800 uppercase tracking-wide">Our Services</span>
            </div>
          </div>

          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900" style={{ fontFamily: "'Cinzel', serif" }}>
            Choose Your Path to <strong className="text-rose-800">Clarity</strong>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Tailored services designed to help you gain the <strong className="text-rose-700">clarity</strong> you need for your arranged marriage journey
          </p>
        </div>

        {/* 1-on-1 Clarity Sessions */}
        <div className="mb-20">
          <h3 className="text-3xl font-bold mb-8 text-gray-900 flex items-center gap-3" style={{ fontFamily: "'Cinzel', serif" }}>
            <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-rose-800 to-red-900 rounded-full">
              <i className="ri-user-heart-line text-xl text-white"></i>
            </div>
            1-on-1 <strong className="text-rose-800">Clarity</strong> Sessions
          </h3>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {sessions.map((session, index) => (
              <div 
                key={index}
                className="group bg-white rounded-2xl p-6 border-2 border-rose-100 hover:border-rose-300 hover:shadow-xl transition-all duration-300 cursor-pointer"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-rose-100 to-amber-100 rounded-xl mb-4 group-hover:scale-110 transition-transform">
                  <i className={`${session.icon} text-3xl text-rose-800`}></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">{session.name}</h4>
                <p className="text-sm text-rose-700 font-semibold mb-3">{session.frequency}</p>
                <p className="text-gray-600">{session.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Clarity Classes */}
        <div className="mb-20">
          <h3 className="text-3xl font-bold mb-8 text-gray-900 flex items-center gap-3" style={{ fontFamily: "'Cinzel', serif" }}>
            <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-rose-800 to-red-900 rounded-full">
              <i className="ri-graduation-cap-line text-xl text-white"></i>
            </div>
            <strong className="text-rose-800">Clarity</strong> Classes
          </h3>

          <div className="grid md:grid-cols-3 gap-8">
            {courses.map((course, index) => (
              <div 
                key={index}
                className="group bg-white rounded-2xl p-8 border-2 border-rose-100 hover:border-rose-300 hover:shadow-xl transition-all duration-300 cursor-pointer"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-rose-100 to-amber-100 rounded-xl group-hover:scale-110 transition-transform">
                    <i className={`${course.icon} text-4xl text-rose-800`}></i>
                  </div>
                  <span className="px-3 py-1 bg-rose-100 text-rose-800 text-xs font-semibold rounded-full">
                    {course.level}
                  </span>
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3">{course.name}</h4>
                <p className="text-gray-600 leading-relaxed">{course.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Clarity Clips */}
        <div>
          <h3 className="text-3xl font-bold mb-8 text-gray-900 flex items-center gap-3" style={{ fontFamily: "'Cinzel', serif" }}>
            <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-rose-800 to-red-900 rounded-full">
              <i className="ri-video-line text-xl text-white"></i>
            </div>
            <strong className="text-rose-800">Clarity</strong> Clips
          </h3>

          <div className="grid md:grid-cols-3 gap-6">
            {clips.map((clip, index) => (
              <div 
                key={index}
                className="group bg-white rounded-2xl p-6 border-2 border-rose-100 hover:border-rose-300 hover:shadow-xl transition-all duration-300 cursor-pointer"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-rose-100 to-amber-100 rounded-xl mb-4 group-hover:scale-110 transition-transform">
                  <i className={`${clip.icon} text-3xl text-rose-800`}></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">{clip.name}</h4>
                <p className="text-gray-600">{clip.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Button */}
        <div className="text-center mt-16">
          <a 
            href="#consultation"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-rose-800 to-red-900 text-white rounded-full font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Book Your Clarity Session
            <i className="ri-arrow-right-line text-xl"></i>
          </a>
        </div>
      </div>
    </section>
  );
}
